import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

import styles from "./EntityFilterIndicator.module.css";
import {
  GenericEntity,
  IPaginationParameters,
  IGenericRequestParameters,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { LucideIcon } from "../../icon/LucideIcon";
import { PropertyTranslator } from "../../../api/GenericTranslator";
import { FloatingDiv } from "../../buttons/FloatingDiv/FloatingDiv";
import { NotAvailable } from "../../misc/UIconstants";

export type IsFilteredDefinition<Filters extends StringIndexedDict> = {
  [property in keyof Filters]: (value: Filters[property]) => boolean;
};

type CommonFilterInterfaces<Entity extends GenericEntity> = IPaginationParameters & IGenericRequestParameters<Entity>;

export interface EntityFilterIndicatorProps<Filters extends StringIndexedDict> {
  filters: Filters;
  translatorConsts?: PropertyTranslator<Filters>;
  excludeFilters?: Partial<IsFilteredDefinition<Filters>>;
  overlayPlaycement?: "left" | "right";
  showAlways?: boolean;
  style?: CSSProperties;
}
export const EntityFilterIndicator = <Entity extends GenericEntity, Filters extends StringIndexedDict>({
  filters,
  translatorConsts,
  excludeFilters,
  overlayPlaycement = "right",
  showAlways = false,
  style,
}: EntityFilterIndicatorProps<Filters>) => {
  const memoizedFilters = useMemo(() => filters, [filters]);
  const defaultExclusions: IsFilteredDefinition<CommonFilterInterfaces<Entity>> = useMemo(
    () => ({
      page: (value) => false,
      pageSize: (value) => false,
      sortBy: (value) => false,
      includeCount: (value) => false,
      searchTermIncludeNames: (value) => false,
      searchTermIncludeIds: (value) => false,
      searchTermIncludeUuids: (value) => false,
      searchTermIncludeCustomFieldValues: (value) => false,
    }),
    []
  );
  const [state, setState] = useState<FilterState>();
  const allExclusions = useMemo(
    () => ({ ...defaultExclusions, ...excludeFilters }) as IsFilteredDefinition<Filters>,
    [defaultExclusions, excludeFilters]
  );
  const getActiveFilters = useCallback(
    (filters: Filters) => {
      let currentFilters = 0;
      let activeFilters: Partial<Filters> = {};
      for (const [property, value] of Object.entries(filters)) {
        if (allExclusions.hasOwnProperty(property)) {
          const isFiltering = allExclusions[property](value);
          if (isFiltering) {
            currentFilters += 1;
            // console.log("Active filter per rule:", property, value);
            activeFilters[property as keyof Filters] = value as any;
          }
        } else {
          // console.log("Property - value", property, value);
          if (typeof value === "string") {
            if (value) {
              currentFilters += 1;
              activeFilters[property as keyof Filters] = value as any;
              // console.log("Active string filter:", property, value);
            }
          } else if (Array.isArray(value) && !!value.length) {
            currentFilters += 1;
            activeFilters[property as keyof Filters] = value as any;
            // console.log("Active array filter:", property, value);
          } else {
            if (value !== null) {
              currentFilters += 1;
              activeFilters[property as keyof Filters] = value;
              // console.log("Active filter:", property, value);
            }
          }
        }
      }
      return { currentFilters, activeFilters };
    },
    [allExclusions]
  );

  interface FilterState {
    currentFilters: number;
    activeFilters: Partial<Filters>;
  }

  useEffect(() => {
    const { currentFilters, activeFilters } = getActiveFilters(memoizedFilters);
    setState({ currentFilters: currentFilters, activeFilters: activeFilters });
  }, [memoizedFilters, getActiveFilters]);

  const overlay =
    translatorConsts && state && !!Object.keys(state.activeFilters).length ? (
      // <OverlayTrigger
      //   placement={overlayPlaycement}
      //   trigger={"click"}
      //   overlay={
      //     <Popover id="popover-activeFilters" style={{ width: "500px", overflow: "hidden" }} className="flex">
      //       <strong>Active filters:</strong>
      //       <div className="flex col-nowrap gap-5" style={{ width: "fit-content", overflow: "hidden" }}>
      //         {state &&
      //           Object.entries(state.activeFilters).map(([key, value], i) => (
      //             <div className="flex row-nowrap" key={i}>
      //               <span style={{ whiteSpace: "nowrap" }}>{translatorConsts[key]}:</span>
      //               <code>
      //                 <RenderFilterValue value={value} />
      //               </code>
      //             </div>
      //           ))}
      //       </div>
      //       {/* {state?.activeFilters ? JSON.stringify(state.activeFilters, null, "\t") : "none"} */}
      //     </Popover>
      //   }
      //   // style={{width: "fit-content", cursor: "pointer"}}
      // >
      //   <LucideIcon name="filter" />
      // </OverlayTrigger>
      <FloatingDiv
        placement="bottom-end"
        triggerComponent={({ ref, getReferenceProps, setOpen }) => (
          <div ref={ref} {...getReferenceProps()} style={{ cursor: "pointer" }}>
            <LucideIcon name="filter" />
          </div>
        )}
      >
        {({ setOpen }) => (
          <div
            className="flex col-nowrap gap-5"
            style={{
              height: "fit-content",
              background: "var(--white)",
              padding: 10,
              border: "1px solid var(--gray-300)",
              borderRadius: 5,
              width: "fit-content",
              maxWidth: "60vw",
              maxHeight: "300px",
              overflow: "hidden",
            }}
          >
            <strong>Active filters</strong>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gridAutoRows: "min-content",
                gridAutoFlow: "rows",
                width: "100%",
                height: "fit-content",
                overflow: "auto",
                gap: 5,
                paddingRight: 10,
              }}
            >
              {state &&
                Object.entries(state.activeFilters).map(([key, value], i) => (
                  <React.Fragment key={i}>
                    <div>
                      <samp style={{ whiteSpace: "nowrap", fontSize: "1.25rem", marginTop: 2 }}>
                        {translatorConsts[key]}:
                      </samp>
                    </div>
                    <RenderFilterValue property={key} value={value} />
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}
      </FloatingDiv>
    ) : (
      <LucideIcon name="filter" />
    );

  return (
    <>
      {(showAlways || (state && state.currentFilters > 0)) && (
        <div
          className={styles.filterIndicatorContainer}
          // title={`${currentFilters} active filters`}
          style={style}
        >
          <div title={`${state?.currentFilters || 0} active filters`}>{overlay}</div>
          {state && state.currentFilters > 0 && (
            <div className={`${styles.filterIndicatorNumber} label label-primary`}>{state.currentFilters}</div>
          )}
        </div>
      )}
    </>
  );
};

const RenderFilterValue = ({
  property,
  value,
}: {
  property: string;
  value: string | string[] | number | number[] | boolean | undefined | null | object;
}) => {
  if (value === null || value === undefined) {
    return NotAvailable;
  }

  return (
    <div style={{ overflow: "auto", width: "100%", maxHeight: "300px" }}>
      <pre>{JSON.stringify({ [property]: value }, null, 2)}</pre>
    </div>
  );
};
